import type { CommonQuestion } from "./CommonQuestionsWidget";

export function buildPreAuthorityQuestions(): CommonQuestion[] {
  return [
    {
      label: "How do I get money for immediate expenses?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>Some costs can't wait, like the funeral or the mortgage.</p>
          <p>
            In most cases, money in the estate is locked until a court gives someone legal authority
            to manage it.
          </p>
          <p>Still, some money can move right away.</p>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>
              Life insurance pays the named beneficiary directly, and it doesn't wait for a court.
            </li>
            <li>Joint accounts stay open to the other owner.</li>
            <li>
              POD (Payable on Death) or TOD (Transfer on Death) accounts go straight to the named
              beneficiary.
            </li>
          </ul>
          <p>
            If you pay estate costs from your own pocket, keep every receipt. Eligible estate
            expenses can be paid back from the estate later. Elayne helps you track them in one
            place.
          </p>
        </div>
      ),
    },
    {
      label: "Do I need an attorney?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            It depends on the estate. Many families settle smaller, simpler estates without one.
            Courts have processes families can navigate on their own, and Elayne guides you through
            the steps in plain language.
          </p>
          <p>
            An attorney helps most when the estate has a lot going on: family members who disagree,
            a business in the estate, property in more than one state, or more debt than money. If
            any of that sounds familiar, a consultation can be worth it.
          </p>
          <p>
            You don't have to decide on day one. With Estate Setup, you can book a call with an
            Estate Expert to talk through your situation, and if the estate needs an attorney, we'll
            connect you with one in your state.
          </p>
        </div>
      ),
    },
    {
      label: "What can I do before I have legal authority?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>More than you might think.</p>
          <p>
            You can order death certificates, and it helps to order several, since banks and
            agencies each want their own copy.
          </p>
          <p>
            You can gather what you find: statements, bills, tax returns, and anything that looks
            official. You can also start a list of accounts and property as you come across them.
          </p>
          <p>
            Some things have to wait, like moving money, closing accounts, selling property, and
            paying most debts. Those need legal authority from a court. Acting before you have it
            can create problems later, even when you're trying to help.
          </p>
          <p>
            The early work matters. Everything you gather now makes the court process faster and
            gives you a head start on settling the estate. Elayne keeps it organized so it's ready
            when you are.
          </p>
        </div>
      ),
    },
    {
      label: "What happens to their debts?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            The estate pays them, not you. This is the part most families are relieved to hear: you
            don't inherit someone's debts just because you're related to them. Credit cards, medical
            bills, and personal loans get paid from the money and property the person who passed
            left behind.
          </p>
          <p>
            There are exceptions. If you co-signed a loan or held a joint credit card, that debt is
            yours too. And if the estate has more debt than money, state law sets the order bills
            get paid. Some may not get paid at all, and that can be okay.
          </p>
          <p>
            One caution: debt collectors sometimes call family members and press them to pay. You
            can ask them to put everything in writing. Knowing the estate's real picture before
            paying anything protects you.
          </p>
        </div>
      ),
    },
  ];
}

export function buildLettersAuthorityQuestions(personName: string): CommonQuestion[] {
  return [
    {
      label: "How long does settling an estate take?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Most estates take about a year, sometimes longer. Estates with property to sell,
            investments to value, or taxes to sort out often run 12 to 18 months.
          </p>
          <p>
            Part of the timeline is set by law. Most states require a waiting period so creditors
            have time to make claims, often a few months. That window runs no matter how organized
            you are, so a stretch of quiet in the middle is normal, not a sign something is wrong.
          </p>
          <p>
            The parts you control move faster with a clear picture of the estate. Elayne's roadmap
            shows what's done, what's next, and what's just waiting on the clock, so you always know
            where things stand.
          </p>
        </div>
      ),
    },
    {
      label: "How do I find accounts I don't know about?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Most families start with mail, old statements, and tax returns. Those catch a lot, but
            not what's gone paperless, and not accounts no one ever mentioned.
          </p>
          <p>
            With your legal authority, Elayne Asset Search requests 10 years of {personName}'s IRS
            records, which show the accounts that banks, employers, and insurers reported. Accounts
            are found for you, including ones with no paper trail at all. Elayne has found an
            estimated $5M+ for families this way.
          </p>
          <p>
            This matters more than it seems. Settling an estate from an incomplete picture means
            money can be left behind for good. Knowing you have the real picture is worth having
            before anything closes.
          </p>
        </div>
      ),
    },
    {
      label: `Do I have to file taxes for ${personName}?`,
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Usually yes, one last personal return. It covers income from January 1 up to the date
            they passed, and it's due at the normal tax deadline the following year. If they were
            owed a refund, the estate collects it.
          </p>
          <p>
            Sometimes the estate itself needs a return too. That happens when the estate earns
            income after the death, like interest, rent, or gains from selling property. This return
            is separate and uses the estate's own tax ID (EIN), which Elayne applies for on your
            behalf.
          </p>
          <p>
            Federal estate tax worries many families, but it only applies to estates worth more than
            $15 million. A few states have their own estate or inheritance taxes with lower limits.
            Elayne flags which returns apply to your situation.
          </p>
        </div>
      ),
    },
    {
      label: "When can I give money to family?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Near the end of settling the estate, in most cases. Debts, taxes, and court requirements
            come first. If money goes out early and a bill shows up later, the person who
            distributed it can be on the hook personally. Waiting protects you.
          </p>
          <p>
            The usual order: the creditor claim window closes, valid debts and taxes get paid, and
            the court or the will's terms clear the way. Then what's left goes to the people named
            in the will, or to heirs under state law if there isn't one.
          </p>
          <p>
            Some states allow partial distributions earlier when the estate clearly has enough to
            cover everything. Elayne's roadmap shows where the estate stands, so you can see when
            giving money is safe rather than guessing. Family will understand a short wait better
            than a problem later.
          </p>
        </div>
      ),
    },
  ];
}

export function buildTrustAuthorityQuestions(personName: string): CommonQuestion[] {
  return [
    {
      label: "Do I still need probate if there's a trust?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Often not, but it depends on what {personName} owned outside the trust. Anything in
            their name alone may still need probate. The search will show you what exists and where
            it sits, so you can decide with facts instead of guessing. If probate turns out to be
            needed, help filing is included at no extra cost.
          </p>
        </div>
      ),
    },
    {
      label: "What will Elayne Asset Search find?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            The retirement plans, bank accounts, life insurance, and property {personName} left
            behind, including ones families often don't know about. Elayne requests 10 years of{" "}
            {personName}'s IRS records and checks 100+ other sources, and each finding comes with
            where it came from.
          </p>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>Retirement accounts, like older 401(k)s or pensions from past employers</li>
            <li>Bank and brokerage accounts, including CDs</li>
            <li>Life insurance and annuities</li>
            <li>Property</li>
            <li>Unclaimed money that's been turned over to the state</li>
          </ul>
          <p>
            It doesn't find safe deposit boxes, physical belongings, private loans, or accounts not
            yet turned over to the state. Knowing what exists before anything is closed keeps money
            from being left behind.
          </p>
        </div>
      ),
    },
    {
      label: "Do I have to pay for this myself?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            No. Elayne is an eligible estate expense, meaning the estate pays for it and not you
            personally. You can pay directly from estate funds, or pay out of pocket now and
            reimburse yourself once the estate bank account is open. Executors handle funeral bills
            and urgent utilities the exact same way.
          </p>
          <p>The single flat fee of $3,000 is the only cost at this stage.</p>
        </div>
      ),
    },
  ];
}

export function buildSomeoneElseQuestions(nomineeName: string): CommonQuestion[] {
  return [
    {
      label: `What can I do before ${nomineeName} joins?`,
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            More than you'd think. Upload any documents you find, add family contacts, and note
            accounts you know about. It all builds the estate record, and none of it needs legal
            authority. When {nomineeName} arrives, the searching and filing start from what you've
            built.
          </p>
        </div>
      ),
    },
    {
      label: "Do I have to pay for this myself?",
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            No. The search is an eligible estate expense: pay from estate funds, or pay now and
            reimburse yourself once the estate account is open, the way executors handle funeral
            bills. $3,000 is the only cost at this stage.
          </p>
        </div>
      ),
    },
    {
      label: `Will I still have access once ${nomineeName} joins?`,
      answer: (
        <div className="flex flex-col gap-3">
          <p>
            Yes. {nomineeName} can see what you've added and you keep your account access. Estates
            work better when the family shares one picture instead of scattered texts and calls.
          </p>
        </div>
      ),
    },
  ];
}
